import React, { useState } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Sparkles, FileText, Tag, Star, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

function AdminAIContentPage() {
  const [generating, setGenerating] = useState(false);
  const [generatedBlogs, setGeneratedBlogs] = useState([]);
  const [metaGenerating, setMetaGenerating] = useState(false);
  const [metaStats, setMetaStats] = useState(null);

  const token = localStorage.getItem('admin_token');

  const generateBlogs = async () => {
    setGenerating(true);
    setGeneratedBlogs([]);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-blogs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({ count: 4 })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate blogs');
      }

      setGeneratedBlogs(data.articles);
      toast.success(`Successfully generated ${data.articles_created} blog articles!`, {
        description: 'Articles have been saved and are live on your blog.'
      });

    } catch (error) {
      console.error('Error generating blogs:', error);
      toast.error('Failed to generate blogs', {
        description: error.message || 'Please check your OpenAI API key and try again.'
      });
    } finally {
      setGenerating(false);
    }
  };

  const generateAllMetaDescriptions = async () => {
    setMetaGenerating(true);
    setMetaStats(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-all-meta-descriptions`,
        {
          method: 'POST',
          headers: {
            'Authorization': token
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate meta descriptions');
      }

      setMetaStats(data);
      toast.success('Meta descriptions generated!', {
        description: `Processed ${data.processed} products`
      });

    } catch (error) {
      console.error('Error generating meta descriptions:', error);
      toast.error('Failed to generate meta descriptions', {
        description: error.message
      });
    } finally {
      setMetaGenerating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
            AI Content Generator
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Auto-generate blog articles and SEO content using AI
          </p>
        </div>

        {/* Blog Generation Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <FileText className="w-5 h-5" />
              Blog Article Generator
            </CardTitle>
            <CardDescription className="text-sm">
              Generate 3-4 engaging blog articles about your products. AI creates diverse content including buying guides, usage tips, educational content, and seasonal guides.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-950/20 p-3 sm:p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
                <strong>Smart Feature:</strong> Automatically tracks which products have been featured in blogs to avoid repetition.
              </p>
            </div>

            <Button
              onClick={generateBlogs}
              disabled={generating}
              className="w-full sm:w-auto"
              size="lg"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Articles...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate 4 Blog Articles
                </>
              )}
            </Button>

            {/* Generated Blogs Display */}
            {generatedBlogs.length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-base sm:text-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Generated Articles ({generatedBlogs.length})
                </h3>
                <div className="grid gap-3">
                  {generatedBlogs.map((article, index) => (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm sm:text-base line-clamp-2">
                              {article.title}
                            </h4>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">
                              {article.excerpt}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                {article.category}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {article.reading_time} min read
                              </Badge>
                              {article.featured_product_ids && (
                                <Badge variant="outline" className="text-xs">
                                  {article.featured_product_ids.length} products featured
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/blog/${article.slug}`, '_blank')}
                          >
                            View
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SEO Meta Descriptions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Tag className="w-5 h-5" />
              SEO Meta Descriptions
            </CardTitle>
            <CardDescription className="text-sm">
              Auto-generate SEO-optimized meta descriptions for all products (150-160 characters).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-purple-50 dark:bg-purple-950/20 p-3 sm:p-4 rounded-lg border border-purple-200 dark:border-purple-800">
              <p className="text-xs sm:text-sm text-purple-900 dark:text-purple-100">
                <strong>SEO Boost:</strong> Each product gets a unique, keyword-rich meta description to improve search engine rankings.
              </p>
            </div>

            <Button
              onClick={generateAllMetaDescriptions}
              disabled={metaGenerating}
              variant="secondary"
              className="w-full sm:w-auto"
              size="lg"
            >
              {metaGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Meta Descriptions...
                </>
              ) : (
                <>
                  <Tag className="w-4 h-4 mr-2" />
                  Generate All Meta Descriptions
                </>
              )}
            </Button>

            {metaStats && (
              <Card className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-sm sm:text-base">{metaStats.message}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {metaStats.processed} products updated with SEO meta descriptions
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {/* Review Summarization Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Star className="w-5 h-5" />
              Review Summarization
            </CardTitle>
            <CardDescription className="text-sm">
              AI automatically summarizes customer reviews on product pages.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-amber-50 dark:bg-amber-950/20 p-3 sm:p-4 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm text-amber-900 dark:text-amber-100">
                  <p className="font-semibold mb-1">Auto-Active Feature</p>
                  <p>
                    Review summaries are automatically generated when customers visit product pages. 
                    No manual action needed. Summaries are cached for 24 hours for performance.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminAIContentPage;
